import {
  OverallRunSuccessRateHeader,
  PerformanceCardDetails,
  PieChartCard,
} from '@/components';
import { useGetChartColorsAndLabel } from '@/hooks';
import { getOverallRunsSucessRateColumns } from '@/modules/Automations/pages/AutomationsPage/utils';
import { useGetProcess } from '@/modules/Processes/queries';
import { useProcessOverallRuns } from '@/modules/Processes/queries/use-process-automations-performance';
import { ExceptionType, ResultFilter, Success } from '@/shared/types';
import { useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

type ProcessOverallSuccessRateCardProps = {
  processId: number;
};

export const ProcessOverallSuccessRateCard = ({
  processId,
}: ProcessOverallSuccessRateCardProps) => {
  const { t } = useTranslation();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [search, setSearch] = useState<string>();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [selectedResult, setSelectedResult] = useState<ResultFilter>();

  const { rateChart } = useGetChartColorsAndLabel();

  const { data: process, isLoading } = useGetProcess(processId);

  const {
    data,
    isLoading: isLoadingOverallRuns,
    fetchNextPage: fetchOverallRunsNextPage,
    isFetching,
    isFetchingNextPage,
  } = useProcessOverallRuns({
    processId,
    enabled: isDetailsOpen,
    dateRange: dateRange,
    filter: search,
    caseResult: selectedResult === Success.Success ? true : undefined,
    exceptionType:
      !!selectedResult &&
      [ExceptionType.Business, ExceptionType.Generic].includes(selectedResult)
        ? selectedResult
        : undefined,
  });

  const overallSuccessRate = useMemo(
    () => data?.pages.flatMap((page) => page) || [],
    [data?.pages],
  );

  const totalMetrics = useMemo(() => {
    const {
      totalBusinessExceptionsCases,
      totalSystemExceptionsCases,
      totalSuccessfulCases,
    } = process
      ? process.executionMetrics
      : {
          totalBusinessExceptionsCases: 0,
          totalSystemExceptionsCases: 0,
          totalSuccessfulCases: 0,
        };
    return {
      totalCases:
        totalSuccessfulCases +
        totalSystemExceptionsCases +
        totalBusinessExceptionsCases,
      totalRuns: process?.executionMetrics.totalRuns ?? 0,
      businessErrors: totalBusinessExceptionsCases,
      genericErrors: totalSystemExceptionsCases,
      successes: totalSuccessfulCases,
    };
  }, [process]);

  return (
    <>
      <PieChartCard
        data={[
          {
            name: rateChart.successes.name,
            fill: rateChart.successes.fill,
            value: process?.executionMetrics.totalSuccessfulCases ?? 0,
          },
          {
            name: rateChart.businessErrors.name,
            fill: rateChart.businessErrors.fill,
            value: process?.executionMetrics.totalBusinessExceptionsCases ?? 0,
          },
          {
            name: rateChart.genericErrors.name,
            fill: rateChart.genericErrors.fill,
            value: process?.executionMetrics.totalSystemExceptionsCases ?? 0,
          },
        ]}
        chartTitle={t('overall_success_rate')}
        valueType="percentage"
        subtitle={t('cases')}
        onExpand={() => setIsDetailsOpen(true)}
      />
      {isDetailsOpen && (
        <PerformanceCardDetails
          title={t('overall_success_rate')}
          subTitle={t('cases')}
          description={t('overall_run_success_rate_description')}
          columns={getOverallRunsSucessRateColumns()}
          onClose={() => setIsDetailsOpen(false)}
          data={{
            items: overallSuccessRate ?? [],
            totalPages: 1,
            totalRows: overallSuccessRate?.length ?? 0,
          }}
          metrics={
            !isLoading && (
              <OverallRunSuccessRateHeader
                key="overallRunSuccessMetric"
                metrics={totalMetrics}
              />
            )
          }
          kpiDateRange={dateRange}
          setKpiDateRange={setDateRange}
          fetchNextPage={fetchOverallRunsNextPage}
          contextType="overallRuns"
          selectedResult={selectedResult}
          setSelectedResult={setSelectedResult}
          isLoading={isLoadingOverallRuns}
          setSearchInput={setSearch}
          isFetching={isFetching}
          isFetchingNextPage={isFetchingNextPage}
        />
      )}
    </>
  );
};
